/**
 * wcagContrast.js — Helper de calcul de contraste WCAG 2.1.
 *
 * Sert au picker de couleur d'accent (Configuration → Page client) pour
 * avertir de manière non-bloquante quand la couleur choisie produit un
 * texte blanc peu lisible.
 *
 * Spec officielle : https://www.w3.org/TR/WCAG21/#contrast-minimum
 */

/**
 * Parse une couleur hex (#RGB ou #RRGGBB) en composantes RGB 0-255.
 *
 * @param {string} hex
 * @returns {{ r: number, g: number, b: number } | null} null si invalide
 */
function parseHexColor(hex) {
  if (typeof hex !== 'string') return null;
  const trimmed = hex.trim();
  // #RRGGBB
  let m = /^#([0-9a-f]{6})$/i.exec(trimmed);
  if (m) {
    const v = m[1];
    return {
      r: parseInt(v.slice(0, 2), 16),
      g: parseInt(v.slice(2, 4), 16),
      b: parseInt(v.slice(4, 6), 16),
    };
  }
  // #RGB → expansion (chaque digit dupliqué)
  m = /^#([0-9a-f]{3})$/i.exec(trimmed);
  if (m) {
    const v = m[1];
    return {
      r: parseInt(v[0] + v[0], 16),
      g: parseInt(v[1] + v[1], 16),
      b: parseInt(v[2] + v[2], 16),
    };
  }
  return null;
}

/**
 * Luminance relative WCAG d'une couleur RGB 0-255.
 *
 * @param {{ r: number, g: number, b: number }} rgb
 * @returns {number} luminance entre 0 (noir) et 1 (blanc)
 */
function relativeLuminance({ r, g, b }) {
  const channel = (c8) => {
    const c = c8 / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/**
 * Calcule le ratio de contraste WCAG entre 2 couleurs hex.
 * Formule officielle WCAG 2.1.
 *
 * @param {string} hex1 - couleur #RRGGBB ou #RGB
 * @param {string} hex2 - couleur #RRGGBB ou #RGB
 * @returns {number | null} ratio entre 1 (aucun contraste) et 21 (max).
 *                          null si l'une des couleurs est invalide.
 */
export function calculateContrastRatio(hex1, hex2) {
  const c1 = parseHexColor(hex1);
  const c2 = parseHexColor(hex2);
  if (!c1 || !c2) return null;
  const l1 = relativeLuminance(c1);
  const l2 = relativeLuminance(c2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Vérifie si un ratio passe le seuil WCAG AA pour le texte normal (≥ 4.5:1).
 *
 * @param {number} ratio
 * @returns {boolean}
 */
export function passesWCAG_AA(ratio) {
  return typeof ratio === 'number' && Number.isFinite(ratio) && ratio >= 4.5;
}
